import type { Order, TrackingEvent } from './types';
import { DELEO_STATUS_TO_EVENT_TYPE, DELEO_DELIVERED_STATUS } from '../../../constants/order';
import { formatLongDate } from '../../../utils/date';

/**
 * Transforms Deleo trace events to UI TrackingEvent format
 */
export const transformTrackingEvents = (order: Order): TrackingEvent[] => {
  const traceList = order.deleoData?.deliveryTrace?.traceInfoList?.[0]?.traceList;

  if (!traceList || traceList.length === 0) {
    return [];
  }

  return traceList.map((event, index) => {
    const isFirstEvent = index === 0;
    const isDelivered = event.status === DELEO_DELIVERED_STATUS;

    const eventStatus = (() => {
      if (isFirstEvent && isDelivered) return 'completed';
      if (isFirstEvent) return 'current';
      return 'completed';
    })();

    const eventType = DELEO_STATUS_TO_EVENT_TYPE[event.status] ?? 'other';

    return {
      id: `${index}-${event.eventTime}`,
      type: eventType,
      title: event.description,
      timestamp: event.eventTime,
      status: eventStatus,
    };
  });
};

/**
 * Gets the tracking number from the order
 */
export const getTrackingNumber = (order: Order): string => {
  return order.deleoData?.deleoTrackingNo ?? '';
};

/**
 * Gets the delivery date from the order tracking events
 */
export const getDeliveryDate = (order: Order): string | undefined => {
  const traceList = order.deleoData?.deliveryTrace?.traceInfoList?.[0]?.traceList;
  const deliveredEvent = traceList?.find((event) => event.status === DELEO_DELIVERED_STATUS);

  if (deliveredEvent?.eventTime) {
    const date = new Date(deliveredEvent.eventTime.replace(' ', 'T'));
    return formatLongDate(date);
  }

  return undefined;
};

/**
 * Gets the order date formatted
 */
export const getFormattedOrderDate = (order: Order): string => {
  const date = new Date(order.orderDateTime ?? order.createdAt);
  return formatLongDate(date);
};

/**
 * Calculates the item price from line item data
 * Uses direct price if available, otherwise calculates from total and quantity
 */
const calculateItemPrice = (item: Order['lineItems'][0]): number => {
  if (item.price !== undefined) {
    return item.price;
  }

  if (item.total !== undefined && item.quantity > 0) {
    return item.total / item.quantity;
  }

  return 0;
};

/**
 * Transforms Order API response to display format for OrderCard
 */
export const transformOrderForDisplay = (order: Order) => {
  return {
    id: Number(order.orderId),
    orderNumber: `#${order.orderId}`,
    status: order.status,
    orderDate: getFormattedOrderDate(order),
    deliveryDate: getDeliveryDate(order) ?? 'Pending',
    trackingNumber: getTrackingNumber(order),
    total: order.totalAmount,
    currency: order.currency,
    items: order.lineItems.map((item, index) => ({
      id: index,
      name: item.name,
      imageUrl: item.wooCommerceImageUrl,
      variant: [item.productSize, item.productPack].filter(Boolean).join(' '),
      quantity: item.quantity,
      price: calculateItemPrice(item),
    })),
    trackingEvents: transformTrackingEvents(order),
  };
};

